import assert from 'assert';
import React, { useMemo, useRef, useState } from 'react';
import { useDynamicContext } from '@dynamic-labs/sdk-react-core';
import { captureException } from '@sentry/react';
import type { WalletClient } from '@wagmi/core';
import ms from 'ms';
import { useSnapshot } from 'valtio';
import { useAccount, useDisconnect, useSwitchNetwork } from 'wagmi';
import { usePublicClient } from 'wagmi';
import type { Address } from 'wagmi';

import { gql } from '@soundxyz/gql-string';
import { exhaustiveGuard } from '@soundxyz/utils';
import { chainIds, getChainName } from '@soundxyz/utils/constants/constants';
import {
  AUTH_ERROR_ACTIONS,
  AUTH_INFO_ACTIONS,
  ERROR_TYPE,
  PILLARS,
} from '@soundxyz/utils/constants/logging';
import { createDeferredPromise } from '@soundxyz/utils/promise';
import { useLatestRef } from '@components-shared/src/hooks/useLatestRef';
import { useSafeLayoutEffect } from '@components-shared/src/hooks/useSafeLayoutEffect';
import { useStableCallback } from '@components-shared/src/hooks/useStableCallback';
import { FilteredActivityFeedTypes } from '@components/Feed/store';
import { setActivityFeedFilter } from '@components/Feed/useActivityFeed';
import { clearFields } from '@components/Profile/Edit/form/helpers';
import { SwitchChainModal } from '@components/modals/SwitchChain';
import type { OnSwitchSuccess } from '@components/modals/SwitchChain';
import { SwitchChainInWalletModal } from '@components/modals/SwitchChainInWallet';
import { AuthUserDocument, fetchGQL, GraphQLReactQuery, LogoutDocument } from '@graphql/reactQuery';
import type { AuthUserQuery } from '@graphql/reactQuery';
import { useLogError } from '@hooks/useLogErrorNew';
import { useLogInfo } from '@hooks/useLogInfo';
import { AnalyticsClient } from '@lib/analytics';
import { setStatsigUser } from '@store/statsig';
import { AVAILABLE_CHAIN_IDS, PRIMARY_CHAIN_ID } from '@utils/constants';
import { minimalRetry } from '@utils/minimalRetry';

import type { PromiseOrValue } from '@utils/types';
import { createContainer } from '../../utils/unstated';
import { graphqlClientHeaders } from '../headers';
import { ModalContext, useModal } from '../modal';
import { useSecondaryModal } from '../secondaryModal';
import { useToast } from '../toast';
import { resetTransactionStatusStore } from '../transaction';
import { useAuth } from './base';
import { AuthEffects, invokeAuthEffect } from './effects';
import { removeAuthToken } from './persistence';

/*#__PURE__*/ gql(/* GraphQL */ `
  mutation generateAuthChallenge($publicAddress: String!) {
    generateAuthChallenge(publicAddress: $publicAddress)
  }

  mutation verifyAuthChallenge($publicAddress: String!, $signedMessage: String!) {
    verifyAuthChallenge(publicAddress: $publicAddress, signedMessage: $signedMessage)
  }

  mutation logout {
    logout {
      __typename
    }
  }
`);

const pillar = PILLARS.AUTH;
const isArtistOnlyPage = false;

const ConnectedAuth = createContainer(function useConnectedAuth() {
  const { disconnect } = useDisconnect();
  const { closeModal } = ModalContext.useContainer();
  const logError = useLogError();
  const logInfo = useLogInfo();

  const { authUser } = useAuth();
  const {
    showAuthFlow,
    setShowAuthFlow,
    handleLogOut: walletLogout,
    primaryWallet,
    user: dynamicUser,
    network,
    setNetwork,
  } = useDynamicContext();
  const walletAccount = useAccount();
  const [supportedChains, setSupportedChains] = useState<string[]>([]);
  const latestWalletAccount = useLatestRef(walletAccount);

  const ethProvider = usePublicClient({ chainId: AVAILABLE_CHAIN_IDS.eth });
  const optimismProvider = usePublicClient({ chainId: AVAILABLE_CHAIN_IDS.optimism });
  const baseProvider = usePublicClient({ chainId: AVAILABLE_CHAIN_IDS.base });
  const headers = useSnapshot(graphqlClientHeaders);

  const authToken = headers['auth-token'];

  const isConnectingWallet = walletAccount.isConnecting || walletAccount.isReconnecting;

  const [isLoggingOut, setIsLoggingOut] = React.useState(false);

  const connectedWalletAddress = walletAccount.address?.toLowerCase() as Address | undefined;

  const latestConnectedWalletAddress = useLatestRef(connectedWalletAddress);

  const isMismatch =
    !!authUser &&
    !!connectedWalletAddress &&
    authUser.publicAddress.toLowerCase() !== connectedWalletAddress;

  const logout = useStableCallback(async () => {
    invokeAuthEffect('logoutStart');

    const authToken = graphqlClientHeaders['auth-token'];
    setIsLoggingOut(true);
    closeModal();

    GraphQLReactQuery.removeQueries({
      queryKey: [AuthUserDocument],
    });

    removeAuthToken();

    await Promise.all([
      walletLogout(),
      setActivityFeedFilter(FilteredActivityFeedTypes.Global),
      authToken &&
        fetchGQL(LogoutDocument, {
          headers: {
            ...graphqlClientHeaders,
            ['auth-token']: authToken,
          },
        }).catch(err => {
          logError({
            action: AUTH_ERROR_ACTIONS.LOG_OUT_ERROR,
            error: err,
            errorType: ERROR_TYPE.FETCH_GQL_ERROR,
            level: 'warning',
            message: 'Error occured using fetchGQL with LogoutDocument',
            pillar,
            isArtistOnlyPage,
            indexedTags: {
              authToken,
            },
            unindexedExtra: {
              graphqlClientHeaders,
            },
          });
        }),
      AnalyticsClient.reset(),
    ]);

    disconnect();
    resetTransactionStatusStore();

    await Promise.all([GraphQLReactQuery.resetQueries()]);
    setIsLoggingOut(false);
    setSupportedChains([]);
    invokeAuthEffect('logoutComplete');
    logInfo({
      action: AUTH_INFO_ACTIONS.LOGGED_OUT,
      message: 'Logged out',
      pillar,
      isArtistOnlyPage,
      data: {
        authToken,
      },
    });
  });

  const isAuthenticatedWithWallet = walletAccount.isConnected && !!connectedWalletAddress;

  const connector = walletAccount.connector;

  const latestConnector = useLatestRef(connector);

  const { switchNetworkAsync } = useSwitchNetwork({
    chainId: PRIMARY_CHAIN_ID,
  });
  const activeChainIdValue = network ? Number(network) : undefined;
  const activeChainIdWithFallbackValue = activeChainIdValue ?? PRIMARY_CHAIN_ID;
  const activeChainId = useLatestRef(activeChainIdValue);

  const activeChainIdWithFallback = useLatestRef(activeChainIdWithFallbackValue);

  const getSigner = useStableCallback(async (chainId: number): Promise<WalletClient> => {
    const signer = await latestConnector.current?.getWalletClient({
      chainId,
    });

    if (!signer)
      throw new Error('Missing wallet signer', {
        cause: {
          connector: latestConnector.current,
        },
      });

    /**
     * For WalletConnect powered wallet if users decides to change the network manually in their wallet
     * after we made them switch chain, this is a safety net to make sure we are on the correct chain for a signer
     *
     * Magic does not support this method. See: https://soundxyz.slack.com/archives/C056RSRRTJ4/p1693341396814199?thread_ts=1693341258.479389&cid=C056RSRRTJ4
     */
    if (!isEmbeddedWallet) {
      await signer.switchChain({ id: chainId });
    }

    return signer;
  });

  const provider = useStableCallback((chainId: number) => {
    switch (chainId) {
      case chainIds.GOERLI:
      case chainIds.MAINNET:
        return ethProvider;
      case chainIds.OPTIMISM_GOERLI:
      case chainIds.OPTIMISM:
        return optimismProvider;
      case chainIds.BASE:
      case chainIds.BASE_GOERLI:
        return baseProvider;
      default:
        throw new Error(`Invalid chainId ${chainId}`);
    }
  });

  const isWalletConnectPoweredWallet = useMemo(() => {
    const profile = dynamicUser?.lastVerifiedCredentialId;
    if (!profile) return false;
    const credential = dynamicUser?.verifiedCredentials?.find(
      credential => credential.id === profile,
    );
    if (!credential) return false;

    return (
      credential?.walletProvider === 'walletConnect' ||
      // Coinbase is not WalletConnect. QR Code wallet can be WalletConnect or Coinbase (as per my current understanding)
      (credential?.walletProvider === 'qrCode' && credential.walletName !== 'coinbase') ||
      credential?.walletProvider === 'deepLink'
    );
  }, [dynamicUser?.lastVerifiedCredentialId, dynamicUser?.verifiedCredentials]);

  /**
   * This effect is used to update the supported chains of the wallet
   */
  React.useEffect(() => {
    primaryWallet?.connector
      ?.getSupportedNetworks()
      .then(chains => setSupportedChains(chains || []));
    // we want to update the supported chains when the wallet changes
    // or when active chain in wallet updates
  }, [primaryWallet?.connector, primaryWallet?.chain, activeChainId]);

  const getDeepLink = useStableCallback(() => {
    try {
      const deepLink = primaryWallet?.connector.getDeepLink();

      return deepLink || null;
    } catch (err) {
      logError({
        action: AUTH_ERROR_ACTIONS.LOG_IN_ERROR,
        error: err,
        errorType: ERROR_TYPE.DEEP_LINK_ERROR,
        level: 'warning',
        message: 'Error occured getting deep link',
        pillar,
        isArtistOnlyPage,
        indexedTags: {
          authToken,
        },
      });
      return null;
    }
  });

  const { id: userId, publicAddress: userPublicAddress, email: userEmail } = authUser || {};
  useSafeLayoutEffect(() => {
    if (userId && userPublicAddress) {
      setStatsigUser({
        userID: userId,
        custom: {
          publicAddress: userPublicAddress,
        },
        privateAttributes: {
          email: userEmail || undefined,
        },
      });
    }
  }, [userId, userPublicAddress, userEmail]);

  const hasDynamicUser = !!dynamicUser;

  const loginPromiseRef = useRef<Promise<LoginPromiseValue> | undefined>();

  const [isAuthenticating, setIsAuthenticating] = useState(false);

  /**
   * If the user in the background changes the wallet account,
   * we keep the previous auth user but we disconnect the wallet from interacting
   * to create transactions
   */
  React.useEffect(() => {
    if (showAuthFlow || isAuthenticating) return;

    if (isMismatch) {
      walletLogout().catch(() => null);
    }
  }, [showAuthFlow, isAuthenticating, isMismatch, walletLogout]);

  const { closeCancelModal } = useModal();
  const { openToast } = useToast();

  const executeLogin = useStableCallback(() => {
    setShowAuthFlow(true);

    const existingAuthUserPublicAddress = authUser?.publicAddress;

    const loginDeferredPromise = createDeferredPromise<LoginPromiseValue>();

    const loginPromise = (loginPromiseRef.current = loginDeferredPromise.promise);

    setIsAuthenticating(true);

    loginPromise.then(() => {
      setIsAuthenticating(false);
    });

    const loginPromiseFail = () => {
      loginDeferredPromise.resolve({ status: 'fail' });
      cleanupLoginStopped();
    };

    const cleanupLoginStarted = AuthEffects.loginStart(() => {
      cleanupLoginStarted();

      const cleanupLoginSuccessEnd = AuthEffects.loginSuccessEnd(async user => {
        cleanupLoginStopped();
        cleanupLoginFail();
        cleanupLoginSuccessEnd();
        cleanupLoginSuccessStarted();

        const successTimeout = setTimeout(() => {
          loginDeferredPromise.resolve({
            status: 'fail',
          });
          clearInterval(successInterval);
        }, 2000);

        const successInterval = setInterval(() => {
          if (
            latestWalletAccount.current.isConnected === false ||
            latestWalletAccount.current.connector == null ||
            !latestWalletAccount.current.address
          ) {
            return;
          }

          clearInterval(successInterval);
          clearTimeout(successTimeout);

          if (
            existingAuthUserPublicAddress &&
            latestWalletAccount.current.address.toLowerCase() !== existingAuthUserPublicAddress
          ) {
            closeCancelModal();
            openToast({
              variant: 'warning',
              text: 'Connected with a different wallet. To access your original account, sign in with the correct wallet.',
              duration: ms('7 seconds'),
            });

            GraphQLReactQuery.invalidateQueries({
              type: 'active',
            }).catch(err => {
              logError({
                action: AUTH_ERROR_ACTIONS.LOG_IN_ERROR,
                error: err,
                errorType: ERROR_TYPE.INVALIDATE_QUERIES_ERROR,
                level: 'warning',
                message: 'Error occured calling invalidateQueries',
                pillar,
                isArtistOnlyPage,
                indexedTags: {
                  authToken,
                },
              });
            });
            GraphQLReactQuery.removeQueries({
              type: 'inactive',
            });

            loginDeferredPromise.resolve({
              status: 'mismatch',
              data: {
                authUser: user.authUser,
                connectedWalletAddress: latestWalletAccount.current.address,
              },
            });
          } else {
            setActivityFeedFilter(FilteredActivityFeedTypes.Global);
            clearFields();
            loginDeferredPromise.resolve({
              status: 'success',
              data: {
                authUser: user.authUser,
                connectedWalletAddress: latestWalletAccount.current.address,
              },
            });
            logInfo({
              action: AUTH_INFO_ACTIONS.LOGGED_IN,
              message: 'Successfully logged in',
              pillar,
              isArtistOnlyPage,
              data: {
                connectedWalletAddress: latestWalletAccount.current.address,
                authToken,
              },
            });
          }
        }, 5);
      });

      const cleanupLoginSuccessStarted = AuthEffects.loginSuccessStart(() => {
        cleanupLoginSuccessStarted();
        cleanupLoginStopped();
      });

      const cleanupLoginFail = AuthEffects.loginFail(() => {
        cleanupLoginFail();
        cleanupLoginSuccessEnd();
        loginPromiseFail();
      });
    });

    const cleanupLoginStopped = AuthEffects.loginStop(() => {
      cleanupLoginStopped();
      cleanupLoginStarted();
      loginPromiseFail();
    });

    return loginPromise;
  });

  const login = useStableCallback((): PromiseOrValue<LoginPromiseValue> => {
    // user already logged in and connected to wallet
    if (isAuthenticatedWithWallet && latestConnectedWalletAddress.current && authUser) {
      return {
        status: 'success',
        data: {
          connectedWalletAddress: latestConnectedWalletAddress.current,
          authUser,
        },
      };
    }

    if (showAuthFlow && loginPromiseRef.current) return loginPromiseRef.current;

    return executeLogin();
  });

  const loginRequireConnection = useStableCallback(async () => {
    if (connectedWalletAddress && authUser && connectedWalletAddress === authUser.publicAddress) {
      return {
        authUser,
        connectedWalletAddress,
        stop: false as const,
      };
    }

    const auth = await login();

    if (auth?.status !== 'success') {
      if (auth.status === 'fail') {
        openToast({
          text: 'Please make sure you wallet is connected',
          variant: 'primary',
        });
      }

      return { stop: true } as const;
    }

    return { ...auth.data, stop: false as const };
  });

  const { openModal: openSecondaryModal } = useSecondaryModal();

  const [isSwitchingNetwork, setIsSwitchingNetwork] = useState(false);
  const latestIsSwitchingNetwork = useLatestRef(isSwitchingNetwork);

  const offSwitchingNetworkTimeout = useRef<ReturnType<typeof setTimeout> | undefined>();

  const switchNetworkChain = useStableCallback(async function switchNetworkChain(chainId: number) {
    if (!switchNetworkAsync) {
      setIsSwitchingNetwork(false);

      openToast({
        text: `Connected wallet doesn't support switching network, please try connecting another wallet to continue`,
        variant: 'error',
      });

      return;
    }

    const chain = getChainName(chainId);
    /**
     * For some WalletConnect v2 powered wallets may not support switching network on the fly
     * example MetaMask Mobile, so we need to ask user to switch network manually in their wallet
     */
    if (isWalletConnectPoweredWallet && !supportedChains.includes(chainId.toString())) {
      let body: JSX.Element;

      switch (chain) {
        case 'MAINNET':
        case 'GOERLI': {
          body = <SwitchChainInWalletModal chain="eth" />;
          break;
        }

        case 'OPTIMISM':
        case 'OPTIMISM_GOERLI': {
          body = <SwitchChainInWalletModal chain="optimism" />;
          break;
        }
        case 'BASE':
        case 'BASE_GOERLI': {
          body = <SwitchChainInWalletModal chain="base" />;
          break;
        }
        default: {
          exhaustiveGuard(chain);
        }
      }

      openSecondaryModal('SWITCH_CHAIN_IN_WALLET', {
        body,
      });
      return;
    }

    clearTimeout(offSwitchingNetworkTimeout.current);

    setIsSwitchingNetwork(true);

    function offSwitchingNetwork() {
      clearTimeout(offSwitchingNetworkTimeout.current);
      setIsSwitchingNetwork(false);
    }

    offSwitchingNetworkTimeout.current = setTimeout(offSwitchingNetwork, 10000);

    const switched = await switchNetworkAsync(chainId).finally(offSwitchingNetwork);
    // This is a temporary workaround for Dynamic SDK not updating the network
    // Context: https://soundxyz.slack.com/archives/C03MAV9GAAK/p1693542273155879
    setNetwork(switched.id);

    await minimalRetry(
      () => {
        assert(chainId === activeChainId.current, 'ChainId should be switched');
      },
      {
        attempts: 20,
        interval: 50,
      },
    ).catch(() => {
      const err = Error(
        'Wallet completed network switch promise but the chain was not switched correctly',
      );
      captureException(err, {
        level: 'warning',
        extra: {
          authUserAddress: authUser?.publicAddress ?? null,
          walletAddress: latestConnectedWalletAddress.current ?? null,
          fromChain: activeChainId.current ?? null,
          toChain: chainId,
          dynamicUser: dynamicUser ?? null,
        },
      });

      throw err;
    });
    return switched;
  });

  const expectMatchingChain = useStableCallback(
    (chainId: number, { onSyncSwitchSuccess }: { onSyncSwitchSuccess?: OnSwitchSuccess } = {}) => {
      if (activeChainId.current === chainId) {
        if (latestIsSwitchingNetwork.current) setIsSwitchingNetwork(false);

        return { stop: false } as const;
      }

      const chain = getChainName(chainId);

      /**
       * For some WalletConnect v2 powered wallets may not support switching network on the fly
       * example MetaMask Mobile, so we need to ask user to switch network manually in their wallet
       */
      if (isWalletConnectPoweredWallet && !supportedChains.includes(chainId.toString())) {
        let body: JSX.Element;

        switch (chain) {
          case 'MAINNET':
          case 'GOERLI': {
            body = <SwitchChainInWalletModal chain="eth" />;
            break;
          }

          case 'OPTIMISM':
          case 'OPTIMISM_GOERLI':
          case 'BASE':
          case 'BASE_GOERLI': {
            body = <SwitchChainInWalletModal chain="optimism" />;
            break;
          }

          case 'BASE':
          case 'BASE_GOERLI': {
            body = <SwitchChainInWalletModal chain="base" />;
            break;
          }
          default: {
            exhaustiveGuard(chain);
          }
        }

        openSecondaryModal('SWITCH_CHAIN_IN_WALLET', {
          body,
        });
        return {
          stop: true,
        } as const;
      }

      if (isWalletConnectPoweredWallet) {
        let body: JSX.Element;

        switch (chain) {
          case 'MAINNET':
          case 'GOERLI': {
            body = <SwitchChainModal chain="eth" onSwitchSuccess={onSyncSwitchSuccess} />;
            break;
          }

          case 'OPTIMISM':
          case 'OPTIMISM_GOERLI': {
            body = <SwitchChainModal chain="optimism" onSwitchSuccess={onSyncSwitchSuccess} />;
            break;
          }

          case 'BASE':
          case 'BASE_GOERLI': {
            body = <SwitchChainModal chain="base" onSwitchSuccess={onSyncSwitchSuccess} />;
            break;
          }
          default: {
            exhaustiveGuard(chain);
          }
        }

        openSecondaryModal('SWITCH_CHAIN', {
          body,
        });
        return {
          stop: true,
        } as const;
      }

      const switching = switchNetworkChain(chainId);

      if (!switching) {
        return {
          stop: true,
        } as const;
      }

      setIsSwitchingNetwork(true);

      return {
        stop: false,
        switching,
      } as const;
    },
  );

  const isEmbeddedWallet = useMemo(() => {
    const profile = dynamicUser?.lastVerifiedCredentialId;
    if (!profile) return false;
    const credential = dynamicUser?.verifiedCredentials?.find(
      credential => credential.id === profile,
    );
    if (!credential) return false;
    return (
      credential?.walletProvider === 'custodialService' ||
      // @ts-expect-error Magic did a breaking change. See this for context: https://soundxyz.slack.com/archives/C03MAV9GAAK/p1695333831345639
      credential?.walletProvider === 'embeddedWallet'
    );
  }, [dynamicUser?.lastVerifiedCredentialId, dynamicUser?.verifiedCredentials]);

  return {
    authUser,
    activeChainId,
    activeChainIdWithFallback,
    dynamicUser,
    expectMatchingChain,
    getDeepLink,
    getSigner,
    hasDynamicUser,
    isAuthenticating,
    isConnectingWallet,
    isEmbeddedWallet,
    isLoggingOut,
    isSwitchingNetwork,
    isWalletInstalled: walletAccount.isConnected,
    latestIsSwitchingNetwork,
    login,
    loginRequireConnection,
    logout,
    /** This comes from Dynamic context, providing some low level helpers like dealing with deep linking. */
    primaryWallet,
    provider,
    isWalletConnectPoweredWallet,
    setIsSwitchingNetwork,
    signMessage: () => {
      // https://linear.app/sound/issue/ENG-4660/fallback-rainbowkit-using-feature-flags
      throw new Error('Not implemented');
    },
    switchNetworkChain,
    walletAddress: connectedWalletAddress,
    walletLogout,
    isAuthenticatedWithWallet,
  };
});

export type LoginSuccessValue = {
  authUser: NonNullable<AuthUserQuery['authUser']>;
  connectedWalletAddress: Address;
};

type LoginPromiseValue =
  | {
      status: 'success';
      data: LoginSuccessValue;
    }
  | {
      status: 'mismatch';
      data: LoginSuccessValue;
    }
  | { status: 'fail' };

export const useConnectedAuth = () => ConnectedAuth.useContainer();

export type ConnectedAuthContainerValue = ReturnType<typeof useConnectedAuth>;

export const ConnectedAuthContext = ConnectedAuth.Context;

export const ConnectedAuthProvider = ConnectedAuth.Provider;
